import { ServiceTokenResponse } from './types';

export interface ServiceTokenManagerConfig {
  keepApiUrl: string;
  clientId: string;
  clientSecret: string;
  /**
   * Optional hook fired once per token-exchange HTTP response. The
   * substrate uses this to enforce the X-Sifter-Min-Version contract:
   * if the local build is below the floor declared by Keep, the hook
   * throws and token refresh propagates the error so the running
   * Sifter exits with an upgrade message at the next refresh.
   */
  onResponse?: (response: Pick<Response, 'headers' | 'status'>) => void;
}

export class ServiceTokenManager {
  private token: string | null = null;
  private expiresAt: number = 0;
  private pendingExchange: Promise<string> | null = null;
  private keepApiUrl: string;
  private clientId: string;
  private clientSecret: string;
  private enabled: boolean;
  private onResponse?: (response: Pick<Response, 'headers' | 'status'>) => void;

  constructor(config: ServiceTokenManagerConfig) {
    this.keepApiUrl = config.keepApiUrl.replace(/\/$/, '');
    this.clientId = config.clientId;
    this.clientSecret = config.clientSecret;
    this.enabled = !!(config.clientId && config.clientSecret && config.keepApiUrl);
    this.onResponse = config.onResponse;

    if (!this.enabled) {
      console.warn(
        '[ServiceTokenManager] Keep credentials not configured (KEEP_CLIENT_ID, KEEP_CLIENT_SECRET, KEEP_API_URL). ' +
        'Service-to-service auth is disabled.'
      );
    }
  }

  /**
   * Returns a valid service token, refreshing if needed.
   * Returns null if Keep credentials are not configured (graceful degradation for local dev).
   */
  async getToken(): Promise<string | null> {
    if (!this.enabled) return null;

    // Return cached token if still valid (refresh 30s before expiry)
    if (this.token && Date.now() < this.expiresAt - 30_000) {
      return this.token;
    }

    // Deduplicate concurrent exchange requests
    if (this.pendingExchange) {
      return this.pendingExchange;
    }

    this.pendingExchange = this.exchange();
    try {
      return await this.pendingExchange;
    } finally {
      this.pendingExchange = null;
    }
  }

  private async exchange(): Promise<string> {
    const url = `${this.keepApiUrl}/api/service-tokens`;

    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        clientId: this.clientId,
        clientSecret: this.clientSecret,
      }),
    });
    this.onResponse?.(response);

    const json = await response.json() as {
      success: boolean;
      data: ServiceTokenResponse;
      error?: { code: string; message: string };
    };

    if (!response.ok || !json.success) {
      const msg = json.error?.message ?? `Token exchange failed with status ${response.status}`;
      throw new Error(`[ServiceTokenManager] ${msg}`);
    }

    this.token = json.data.token;
    this.expiresAt = Date.now() + json.data.expiresIn * 1000;

    return this.token;
  }
}
